import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";

const HERO_IMG =
  "https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/z78rjdno_ChatGPT%20Image%20Sep%203%2C%202026%20at%2004_19_19%20PM.png";

const LIFESTYLES = [
  {
    key: "men",
    title: "Men",
    tagline: "Everyday, considered.",
    body: "Premium clothing designed to be worn beyond the gym or trail.",
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/99fxj0o1_ChatGPT%20Image%20Sep%203%2C%202026%20at%2004_07_47%20PM.png",
    tid: HOME.shopFashionBtn,
    to: "/store?gender=men",
  },
  {
    key: "women",
    title: "Women",
    tagline: "Discipline. Movement. Progression.",
    body: "Performance-inspired pieces for the sessions that build the standard.",
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/wz8z1qhp_ChatGPT%20Image%20Jul%2025%2C%202026%20at%2002_29_03%20PM.png",
    tid: HOME.shopTrainingBtn,
    to: "/store?gender=women",
  },
  {
    key: "military",
    title: "Military",
    tagline: "Discipline. Standards. Service.",
    body: "For serving personnel, veterans and anyone who lives by the standard.",
    img: "https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/tvdufygv_ChatGPT%20Image%20Sep%203%2C%202026%20at%2003_48_36%20PM.png",
    tid: HOME.shopOutdoorsBtn,
    to: "/store?collection=Field%20to%20Civilian",
  },
];

const COMMUNITY = [
  "https://images.pexels.com/photos/2294361/pexels-photo-2294361.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1552106/pexels-photo-1552106.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1552252/pexels-photo-1552252.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1552103/pexels-photo-1552103.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1229356/pexels-photo-1229356.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/2247179/pexels-photo-2247179.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1552108/pexels-photo-1552108.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1526790/pexels-photo-1526790.jpeg?auto=compress&cs=tinysrgb&w=800",
];

export default function Home() {
  const [form, setForm] = useState({ first_name: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to receive DTA emails.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/newsletter", form);
      toast.success("Welcome to the adjustment.");
      setForm({ first_name: "", email: "", consent: false });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* ------------------- HERO ------------------- */}
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="A quiet moment before the work"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/70" />
        </div>
        <div className="relative dta-container min-h-[92vh] flex flex-col justify-end pb-16 md:pb-24 pt-28">
          <div className="max-w-4xl animate-in-up">
            <p className="dta-overline text-white/70 mb-6">DTA. Down. Test. Adjust.</p>
            <h1 className="hero-headline text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[10rem]">
              Failure is<br />part of the<br />
              <span className="text-white">process<span className="text-[#8fa8d6]">.</span></span>
            </h1>
            <p className="mt-8 text-base md:text-lg max-w-lg text-white/70 leading-relaxed">
              Military heritage. Civilian resilience. Born from the marksmanship principals, rebuilt for everyday life.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="dta-btn dta-btn-primary"
                data-testid={HOME.heroStoryBtn}
              >
                Discover Our Story <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/support"
                className="dta-btn dta-btn-outline-light"
                data-testid={HOME.findSupportBtn}
              >
                Find Support
              </Link>
            </div>
          </div>
        </div>
        <div className="relative border-t border-white/10">
          <div className="dta-container flex flex-wrap items-center justify-between gap-4 py-5 text-white/50">
            <span className="dta-overline text-[0.65rem]">Est. Britain</span>
            <span className="dta-overline text-[0.65rem] hidden sm:inline">Made to order</span>
            <span className="dta-overline text-[0.65rem] hidden md:inline">Free UK delivery over £75</span>
            <span className="dta-overline text-[0.65rem]">Adjust · Continue forward</span>
          </div>
        </div>
      </section>

      {/* ------------------- BRAND PRINCIPLE ------------------- */}
      <section className="bg-[#F4F3EF] py-24 md:py-32 border-b border-black/10">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <p className="dta-overline text-navy">The Principle</p>
            <h2 className="section-heading text-4xl md:text-5xl lg:text-6xl mt-4">
              Down.<br />
              <span className="text-navy">Test.</span>
              <br />
              Adjust.
            </h2>
          </div>
          <div className="md:col-span-8 md:pt-4 space-y-6 text-[#272727] max-w-2xl">
            <p className="text-lg md:text-xl leading-relaxed">
              Born and developed from the marksmanship principles and rebuilt for everyday life. If you miss the target, you do not abandon the shot.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              Instead you bring the weapon down, you focus on what went wrong, make the adjustments, and go again.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              DTA is built for anyone facing self-doubt, pressure or the battles that others cannot see, both physically and mentally.
            </p>
            <div className="pt-2">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 dta-overline text-navy hover:opacity-70 transition-opacity"
                data-testid={HOME.readStoryBtn}
              >
                Read the DTA story <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------- BUILT FOR EVERYONE ------------------- */}
      <section className="bg-[#0A0A0A] text-white py-24 md:py-32">
        <div className="dta-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="dta-overline text-white/50">Built for</p>
              <h2 className="section-heading text-5xl md:text-7xl mt-3">
                Built for<br />everyone.
              </h2>
            </div>
          </div>

          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {LIFESTYLES.map((l) => (
                <div
                  key={l.key}
                  data-testid={l.tid}
                  className="group relative aspect-[3/4] overflow-hidden bg-black border border-white/10"
                >
                  <img
                    src={l.img}
                    alt={l.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-95"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/75" />
                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                    <p className="dta-overline text-white/60 text-[0.65rem]">{l.tagline}</p>
                    <h3 className="section-heading text-4xl md:text-5xl mt-2">{l.title}</h3>
                    <p className="text-sm text-white/70 mt-3 max-w-xs">{l.body}</p>
                  </div>

                  {/* Per-card diagonal banner — mobile only */}
                  <div
                    aria-hidden="true"
                    className="md:hidden pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
                  >
                    <div
                      className="w-[140%] py-3 rotate-[-14deg] bg-[#031D47]/85 border-y-2 border-[#8fa8d6]/60 backdrop-blur-[1px] shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 12px, transparent 12px 24px)",
                      }}
                    >
                      <p className="text-center font-display font-black uppercase text-white text-2xl sm:text-3xl tracking-[0.08em] leading-none whitespace-nowrap drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                        <span>New Store</span>{" "}
                        <span className="text-[#8fa8d6]">·</span>{" "}
                        <span>Coming Soon</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop diagonal banner spanning all three cards */}
            <div
              aria-hidden="true"
              className="hidden md:flex pointer-events-none absolute inset-0 items-center justify-center overflow-hidden"
            >
              <div
                className="w-[140%] py-4 md:py-6 rotate-[-14deg] bg-[#031D47]/85 border-y-2 border-[#8fa8d6]/60 backdrop-blur-[1px] shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 12px, transparent 12px 24px)",
                }}
              >
                <p className="text-center font-display font-black uppercase text-white text-3xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[0.08em] leading-none whitespace-nowrap drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                  <span className="text-white/90">New Store</span>{" "}
                  <span className="text-[#8fa8d6]">·</span>{" "}
                  <span className="text-white">Coming Soon</span>{" "}
                  <span className="text-[#8fa8d6]">·</span>{" "}
                  <span className="text-white/90">New Store</span>{" "}
                  <span className="text-[#8fa8d6]">·</span>{" "}
                  <span className="text-white">Coming Soon</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products removed — Store launching soon */}

      {/* ------------------- THE DTA PROCESS ------------------- */}
      <section className="bg-[#0A0A0A] text-white py-24 md:py-32 border-t border-white/5 relative grain">
        <div className="dta-container">
          <div className="max-w-2xl">
            <p className="dta-overline text-white/50">The DTA process</p>
            <h2 className="section-heading text-4xl md:text-6xl mt-3">
              Three words.<br />
              One discipline.
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10 border-y md:border md:border-white/10">
            {[
              {
                n: "01",
                title: "Down",
                body:
                  "Recognise the signs, pause everything, see where you are, and try to control the situation.",
              },
              {
                n: "02",
                title: "Test",
                body:
                  "Strength • Cardiovascular Fitness • Mobility • Habits • Nutrition",
                accent: true,
              },
              {
                n: "03",
                title: "Adjust",
                body:
                  "Training • Calories / Macros • Protein • Recovery • Daily Activity",
              },
            ].map((s) => (
              <div key={s.title} className="p-8 md:p-10">
                <div className="flex items-center justify-between">
                  <span className={`dta-overline text-[0.7rem] ${s.accent ? "text-[#8fa8d6]" : "text-white/50"}`}>
                    Stage {s.n}
                  </span>
                </div>
                <h3
                  className={`font-display font-black uppercase text-5xl md:text-6xl mt-4 ${
                    s.accent ? "text-[#8fa8d6]" : "text-white"
                  }`}
                >
                  {s.title}.
                </h3>
                <p className="text-sm md:text-base text-white/70 mt-4 max-w-sm leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------- FAILURE EDITORIAL ------------------- */}
      <section className="bg-[#F4F3EF] py-24 md:py-32">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-6 relative aspect-[4/5] md:aspect-auto md:min-h-[540px] overflow-hidden">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/xqimmzfi_ChatGPT%20Image%20Jul%2025%2C%202026%20at%2002_06_01%20PM.png"
              alt="Press-up in the rain, a quiet moment of preparation"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-6 flex flex-col justify-center">
            <p className="dta-overline text-navy">Editorial</p>
            <h2 className="section-heading text-4xl md:text-6xl mt-4">
              Missing the target<br />is not the end.
            </h2>
            <div className="mt-8 space-y-5 text-[#272727] max-w-lg">
              <p className="text-base md:text-lg leading-relaxed">
                When applying the marksmanship principles, the answer to a missed shot is never to abandon the objective. Instead, the shooter brings the weapon down, assesses the cause, adjusts, and carries on.
              </p>
              <p className="font-display uppercase font-bold text-xl md:text-2xl text-[#031D47]">
                Failure is part of the process.....
              </p>
              <p className="text-base leading-relaxed opacity-80">
                Life often requires the same approach. Failure provides information. Assessment creates understanding. Adjustment creates progress.
              </p>
            </div>
            <div className="mt-10">
              <Link to="/about" className="dta-btn dta-btn-outline-dark">
                Read The Story <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------- COMMUNITY GRID ------------------- */}
      <section className="bg-[#0A0A0A] text-white py-24 md:py-32">
        <div className="dta-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="dta-overline text-white/50">Community</p>
              <h2 className="section-heading text-4xl md:text-6xl mt-3">
                @downtestadjust<br />Real people. Real adjustments.
              </h2>
            </div>
            <a
              href="https://instagram.com/downtestadjust"
              target="_blank"
              rel="noreferrer"
              className="dta-overline text-white/60 hover:text-white inline-flex items-center gap-2"
            >
              Follow on Instagram <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {COMMUNITY.map((src, i) => (
              <a
                key={i}
                href="https://instagram.com/downtestadjust"
                target="_blank"
                rel="noreferrer"
                className="relative aspect-square overflow-hidden bg-black group"
              >
                <img
                  src={src}
                  alt="Community moment"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100 hover-shift"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------- SUPPORT SIGNPOSTING ------------------- */}
      <section className="bg-[#031D47] text-white py-20 md:py-28">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="dta-overline text-white/50">You have to accept</p>
            <h2 className="section-heading text-4xl md:text-6xl mt-3">
              No one is coming<br />to save you.
            </h2>
            <p className="text-white/70 mt-6 max-w-xl leading-relaxed">
              DTA supports open conversations about mental health and encourages people to seek qualified support when they need it. Resilience is not silence.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/support"
              className="dta-btn dta-btn-outline-light"
              data-testid={HOME.findSupportBtn}
            >
              Find Support <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ------------------- EMAIL SIGNUP ------------------- */}
      <section className="bg-[#F4F3EF] py-24 md:py-32">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="dta-overline text-navy">Join The Adjustment</p>
            <h2 className="section-heading text-4xl md:text-6xl mt-3">
              Product releases.<br />
              Stories. Standards.
            </h2>
            <p className="mt-6 text-[#272727] max-w-md leading-relaxed">
              Occasional dispatches from the DTA community. No noise. Unsubscribe any time.
            </p>
          </div>
          <form
            className="md:col-span-7 md:pl-10 md:border-l border-black/10 flex flex-col gap-5"
            onSubmit={submitNewsletter}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex flex-col gap-2">
                <span className="dta-overline text-[0.65rem] text-[#272727]">First name</span>
                <input
                  data-testid={HOME.newsletterFirstName}
                  required
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  type="text"
                  className="bg-transparent border-b border-black/30 focus:border-navy outline-none py-3 text-lg"
                  placeholder="Your first name"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="dta-overline text-[0.65rem] text-[#272727]">Email address</span>
                <input
                  data-testid={HOME.newsletterEmail}
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  type="email"
                  className="bg-transparent border-b border-black/30 focus:border-navy outline-none py-3 text-lg"
                  placeholder="you@domain.co.uk"
                />
              </label>
            </div>
            <label className="flex items-start gap-3 text-sm text-[#272727]">
              <input
                data-testid={HOME.newsletterConsent}
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                className="mt-1 accent-[#031D47]"
              />
              I agree to receive occasional emails from DTA. I can unsubscribe at any time.
            </label>
            <div>
              <button
                type="submit"
                disabled={submitting}
                data-testid={HOME.newsletterSubmit}
                className="dta-btn dta-btn-dark disabled:opacity-60"
              >
                {submitting ? "Adjusting…" : "Join The Adjustment"}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
